import { useScenarioForm } from './contexts';
import { NumberField, TanstackFieldWrapper } from 'components/form-fields-2';
import {
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  Toggle,
  Tooltip,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { ReorderableList } from 'components/reorderable-list/reorderable-list';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import sectionStyles from './config-sections.module.scss';
import styles from './inventory-management.module.scss';
import {
  EurBasis,
  EurStream,
  DepthOrder,
  InventoryConfig,
  InventoryConfigResponse,
  InventoryConfigsResponse,
} from './types';
import { formationPriorityValueToLabel } from './default-values';
import { useEffect, useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import { InventoryManagementSkeleton } from './section-skeletons';
import { useConfigSwitch } from './hooks';

const FALLBACK_ATOM = atom(null);

const EUR_BASIS_OPTIONS: { value: EurBasis; label: string }[] = [
  { value: 'single_slot', label: 'Single Slot' },
  { value: 'formation', label: 'Formation' },
];

const EUR_STREAM_OPTIONS: { value: EurStream; label: string }[] = [
  { value: 'oil', label: 'Oil' },
  { value: 'gas', label: 'Gas' },
  { value: 'boe6', label: 'BOE6' },
  { value: 'boe20', label: 'BOE20' },
];

const DEPTH_ORDER_OPTIONS: { value: DepthOrder; label: string }[] = [
  { value: 'deep_first', label: 'Deep First' },
  { value: 'shallow_first', label: 'Shallow First' },
];

const mapResponseToInventoryConfig = (
  resp: InventoryConfigResponse
): InventoryConfig => ({
  formationPriority: (resp.formationPriorityOrder || []).map(
    formationPriorityValueToLabel
  ),
  eurBasis: resp.eurDefinitionScope,
  eurStream: resp.eurDefinitionStream,
  depthOrder: resp.depthPriority,
  allowOverflow: resp.allowOverflow,
  allowPartialSlots: resp.allowPartialSlots,
  minSlotLength: resp.minimumSlotLengthFt,
  infillAssumptions: {
    includeActiveUpsideLtds: resp.includeActiveUpsideLtds,
    formationLimitPercent: resp.formationLimit,
    developmentAreaLimitPercent: resp.developmentAreaLimit,
    ltdMinimum: resp.ltdMinimum,
  },
});

export const InventoryManagement = () => {
  const {
    form,
    selectedAreaConfig,
    setFieldBaseline,
    readOnly,
    setSavedInventoryConfigHref,
    manualFormationPriority,
  } = useScenarioForm();

  // Fetch existing inventory config from API
  const inventoryConfigHref =
    selectedAreaConfig?.resources?.inventoryConfigs?.href || null;

  const inventoryConfigAtom = useMemo(() => {
    if (!inventoryConfigHref) return FALLBACK_ATOM;
    return createResourceAtom<InventoryConfigsResponse>(inventoryConfigHref);
  }, [inventoryConfigHref]);

  const inventoryConfigData = useAtomValue(inventoryConfigAtom);

  const initializedForConfigRef = useConfigSwitch(selectedAreaConfig);

  // When API data loads, override with fetched values
  useEffect(() => {
    const configId = selectedAreaConfig?.id ?? null;
    if (initializedForConfigRef.current === configId) return;
    if (!inventoryConfigData?.data || !('results' in inventoryConfigData.data))
      return;
    initializedForConfigRef.current = configId;
    const results = inventoryConfigData.data.results;
    if (results.length > 0) {
      const config = mapResponseToInventoryConfig(results[0]);
      form.setFieldValue('inventoryConfig', config);
      setFieldBaseline('inventoryConfig', config);
      setSavedInventoryConfigHref(results[0].href);
    }
  }, [
    inventoryConfigData,
    form,
    setFieldBaseline,
    setSavedInventoryConfigHref,
    selectedAreaConfig?.id,
    initializedForConfigRef,
  ]);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary className={sectionStyles.accordionSummaryBorder}>
        <h3 className={sectionStyles.sectionTitle}>Inventory Management</h3>
      </AccordionSummary>
      <AccordionDetails>
        <div className={sectionStyles.sectionDetails}>
          <p className={styles.description}>
            Configure Inventory projections and how Formations are prioritized
            for placement of Unlanded Wells.
          </p>

          {inventoryConfigData?.loading ? (
            <InventoryManagementSkeleton />
          ) : (
            <form.Subscribe selector={(state) => state.values.inventoryConfig}>
              {(inventoryConfig) => {
                if (!inventoryConfig) return null;

                return (
                  <>
                    {/* Formation Priority */}
                    <p className={styles.fieldLabel}>Formation Priority</p>
                    <form.Field
                      name={'inventoryConfig.formationPriority' as any}
                    >
                      {(field: any) => (
                        <ReorderableList
                          items={field.state.value || []}
                          onReorder={(items) => field.handleChange(items)}
                          disabled={readOnly || manualFormationPriority}
                        />
                      )}
                    </form.Field>

                    {/* EUR */}
                    <form.Field name={'inventoryConfig.eurBasis' as any}>
                      {(field: any) => (
                        <div className={styles.radioGroup}>
                          <RadioButtonGroup
                            legendText="EUR"
                            name="eur-basis"
                            valueSelected={field.state.value}
                            onChange={(value: string | number | undefined) =>
                              field.handleChange(value)
                            }
                            disabled={readOnly}
                          >
                            {EUR_BASIS_OPTIONS.map((opt) => (
                              <RadioButton
                                key={opt.value}
                                id={`eur-basis-${opt.value}`}
                                labelText={opt.label}
                                value={opt.value}
                              />
                            ))}
                          </RadioButtonGroup>
                        </div>
                      )}
                    </form.Field>

                    <form.Field name={'inventoryConfig.eurStream' as any}>
                      {(field: any) => (
                        <div className={styles.radioGroup}>
                          <RadioButtonGroup
                            legendText=""
                            name="eur-stream"
                            valueSelected={field.state.value}
                            onChange={(value: string | number | undefined) =>
                              field.handleChange(value)
                            }
                            disabled={readOnly}
                          >
                            {EUR_STREAM_OPTIONS.map((opt) => (
                              <RadioButton
                                key={opt.value}
                                id={`eur-stream-${opt.value}`}
                                labelText={opt.label}
                                value={opt.value}
                              />
                            ))}
                          </RadioButtonGroup>
                        </div>
                      )}
                    </form.Field>

                    {/* Depth */}
                    <form.Field name={'inventoryConfig.depthOrder' as any}>
                      {(field: any) => (
                        <div className={styles.radioGroup}>
                          <RadioButtonGroup
                            legendText="Depth"
                            name="depth-order"
                            valueSelected={field.state.value}
                            onChange={(value: string | number | undefined) =>
                              field.handleChange(value)
                            }
                            disabled={readOnly}
                          >
                            {DEPTH_ORDER_OPTIONS.map((opt) => (
                              <RadioButton
                                key={opt.value}
                                id={`depth-order-${opt.value}`}
                                labelText={opt.label}
                                value={opt.value}
                              />
                            ))}
                          </RadioButtonGroup>
                        </div>
                      )}
                    </form.Field>

                    {/* Allow overflow */}
                    <form.Field name={'inventoryConfig.allowOverflow' as any}>
                      {(field: any) => (
                        <div className={styles.toggleRow}>
                          <span className={styles.toggleLabel}>
                            Allow overflow
                            <Tooltip
                              align="top"
                              label="If there are more Unlanded Wells than available Slots, additional Slots will be created."
                            >
                              <Information size={16} />
                            </Tooltip>
                          </span>
                          <Toggle
                            id="allowOverflow"
                            size="sm"
                            labelA="Off"
                            labelB="On"
                            toggled={field.state.value ?? false}
                            onToggle={(checked: boolean) => {
                              field.handleChange(checked);
                            }}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </form.Field>

                    {/* Allow partial Slots */}
                    <form.Field
                      name={'inventoryConfig.allowPartialSlots' as any}
                    >
                      {(field: any) => (
                        <div className={styles.toggleRow}>
                          <span className={styles.toggleLabel}>
                            Allow partial Slots
                          </span>
                          <Toggle
                            id="allowPartialSlots"
                            size="sm"
                            labelA="Off"
                            labelB="On"
                            toggled={field.state.value ?? false}
                            onToggle={(checked: boolean) => {
                              field.handleChange(checked);
                            }}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </form.Field>

                    {/* Minimum Slot Length (ft) */}
                    <div className={styles.numberFieldRow}>
                      <form.Field name={'inventoryConfig.minSlotLength' as any}>
                        {(field: any) => (
                          <TanstackFieldWrapper<number | null> field={field}>
                            {(props) => (
                              <NumberField
                                id="minSlotLength"
                                label="Minimum Slot Length (ft)"
                                disabled={readOnly}
                                {...props}
                              />
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>

                    {/* Infill Assumptions */}
                    <h4 className={styles.infillHeading}>Infill Assumptions</h4>

                    {/* Include Active Upside LTDs */}
                    <form.Field
                      name={
                        'inventoryConfig.infillAssumptions.includeActiveUpsideLtds' as any
                      }
                    >
                      {(field: any) => (
                        <div className={styles.checkboxRow}>
                          <Checkbox
                            id="includeActiveUpsideLtds"
                            labelText={
                              <span className={styles.checkboxLabel}>
                                Include Active Upside LTDs
                                <Tooltip
                                  align="top"
                                  label="If checked, active upside LTDs will be included in evaluations and the limit checks below"
                                >
                                  <Information size={16} />
                                </Tooltip>
                              </span>
                            }
                            checked={field.state.value ?? false}
                            onChange={(
                              _: any,
                              { checked }: { checked: boolean }
                            ) => {
                              field.handleChange(checked);
                            }}
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </form.Field>

                    {/* Infill number inputs */}
                    <div className={styles.infillInputsGrid}>
                      <form.Field
                        name={
                          'inventoryConfig.infillAssumptions.formationLimitPercent' as any
                        }
                      >
                        {(field: any) => (
                          <TanstackFieldWrapper<number | null> field={field}>
                            {(props) => (
                              <div>
                                <span className={styles.labelWithIcon}>
                                  <Tooltip
                                    align="top"
                                    label="If >=X% of the slots within a single bench are filled (PDP, DUC, PMT, Shut-In), kill LTDs in that bench"
                                  >
                                    <Information size={16} />
                                  </Tooltip>
                                  <span className={styles.fieldLabel}>
                                    Formation Limit (%)
                                  </span>
                                </span>
                                <NumberField
                                  id="formationLimitPercent"
                                  disabled={readOnly}
                                  isAllowed={(v) => v >= 0 && v <= 100}
                                  {...props}
                                />
                              </div>
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>

                      <form.Field
                        name={
                          'inventoryConfig.infillAssumptions.developmentAreaLimitPercent' as any
                        }
                      >
                        {(field: any) => (
                          <TanstackFieldWrapper<number | null> field={field}>
                            {(props) => (
                              <div>
                                <span className={styles.labelWithIcon}>
                                  <Tooltip
                                    align="top"
                                    label="If >=X% of the slots in the unit are filled, kill LTDs in the unit."
                                  >
                                    <Information size={16} />
                                  </Tooltip>
                                  <span className={styles.fieldLabel}>
                                    Development Area Limit (%)
                                  </span>
                                </span>
                                <NumberField
                                  id="developmentAreaLimitPercent"
                                  disabled={readOnly}
                                  isAllowed={(v) => v >= 0 && v <= 100}
                                  {...props}
                                />
                              </div>
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>

                      <form.Field
                        name={
                          'inventoryConfig.infillAssumptions.ltdMinimum' as any
                        }
                      >
                        {(field: any) => (
                          <TanstackFieldWrapper<number | null> field={field}>
                            {(props) => (
                              <div>
                                <span className={styles.labelWithIcon}>
                                  <Tooltip
                                    align="top"
                                    label="If there are fewer than X LTDs in the unit, kill them"
                                  >
                                    <Information size={16} />
                                  </Tooltip>
                                  <span className={styles.fieldLabel}>
                                    LTD Minimum
                                  </span>
                                </span>
                                <NumberField
                                  id="ltdMinimum"
                                  disabled={readOnly}
                                  decimalScale={0}
                                  {...props}
                                />
                              </div>
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    </div>
                  </>
                );
              }}
            </form.Subscribe>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};
